import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarClock,
  ExternalLink,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import {
  CONTACT_TEAM_WHATSAPP_DEEP_LINK,
  CONTACT_TEAM_WHATSAPP_URL,
  CONTACT_TEAM_X_DM_DEEP_LINK,
  CONTACT_TEAM_X_DM_URL,
  TALK_TO_HUMAN_BOOKING_EMBED_URL,
  TALK_TO_HUMAN_BOOKING_URL,
  TALK_TO_HUMAN_SUBSCRIBE_URL,
} from '../lib/talkToHuman';
import { useAuthenticatedFetch } from '../utils/apiClient';

type Props = {
  className?: string;
  label?: string;
  triggerVariant?: 'button' | 'inline-email';
  inlineEmailPlaceholder?: string;
  prefillEmail?: string | null;
  showSubscribeDirect?: boolean;
  onOpen?: () => void;
};

type ContactView = 'menu' | 'feedback' | 'scheduler';

const FEEDBACK_MIN_CHARS = 5;
const FEEDBACK_MAX_CHARS = 4000;
const APP_FALLBACK_DELAY_MS = 900;

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.error === 'string') return payload.error;
  } catch {
    // Fall back to status-based error.
  }
  return `Request failed with status ${response.status}`;
};

export default function TalkToHumanButton({
  className = 'inline-flex min-h-[38px] items-center gap-2 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]',
  label = 'Contact Team',
  triggerVariant = 'button',
  inlineEmailPlaceholder = 'name@company.com',
  prefillEmail = null,
  showSubscribeDirect = true,
  onOpen,
}: Props) {
  const { userEmail } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const location = useLocation();

  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ContactView>('menu');
  const [capturedEmail, setCapturedEmail] = useState<string | null>(prefillEmail?.trim() || userEmail || null);

  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);

  const [embedLoaded, setEmbedLoaded] = useState(false);
  const [fallbackOpened, setFallbackOpened] = useState(false);

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setCapturedEmail(prefillEmail?.trim() || userEmail || null);
  }, [prefillEmail, userEmail]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, view]);

  const openModal = () => {
    setCapturedEmail(prefillEmail?.trim() || userEmail || null);
    setView('menu');
    setFeedbackMessage('');
    setFeedbackError(null);
    setFeedbackSuccess(false);
    setFeedbackBusy(false);
    setEmbedLoaded(false);
    setFallbackOpened(false);
    onOpen?.();
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const openExternalLink = (url: string) => {
    const normalizedUrl = (url || '').trim();
    if (!normalizedUrl) return;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  };

  const openAppWithWebFallback = (appUrl: string, webUrl: string) => {
    const normalizedAppUrl = (appUrl || '').trim();
    const normalizedWebUrl = (webUrl || '').trim();
    if (!normalizedAppUrl && !normalizedWebUrl) return;
    if (!normalizedAppUrl) {
      if (normalizedWebUrl) window.location.assign(normalizedWebUrl);
      return;
    }

    let fallbackTimer: number | null = null;
    const clearFallback = () => {
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };
    const cleanup = () => {
      clearFallback();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', cleanup);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') cleanup();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', cleanup);

    fallbackTimer = window.setTimeout(() => {
      cleanup();
      if (normalizedWebUrl) window.location.assign(normalizedWebUrl);
    }, APP_FALLBACK_DELAY_MS);

    try {
      window.location.assign(normalizedAppUrl);
    } catch {
      cleanup();
      if (normalizedWebUrl) window.location.assign(normalizedWebUrl);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!capturedEmail) {
      setFeedbackError('Log in to send feedback to the team.');
      return;
    }

    const normalizedMessage = feedbackMessage.trim();
    if (normalizedMessage.length < FEEDBACK_MIN_CHARS) {
      setFeedbackError(`Please enter at least ${FEEDBACK_MIN_CHARS} characters.`);
      return;
    }
    if (normalizedMessage.length > FEEDBACK_MAX_CHARS) {
      setFeedbackError(`Please keep feedback under ${FEEDBACK_MAX_CHARS} characters.`);
      return;
    }

    setFeedbackBusy(true);
    setFeedbackError(null);
    setFeedbackSuccess(false);
    try {
      const response = await authenticatedFetch('/api/me/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: normalizedMessage,
          page_path: `${location.pathname}${location.search || ''}`,
          source: 'contact_team_modal',
          channel: 'feedback_request',
        }),
      });

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      setFeedbackMessage('');
      setFeedbackSuccess(true);
    } catch (error) {
      setFeedbackError(error instanceof Error ? error.message : 'Failed to send feedback.');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const title =
    view === 'feedback' ? 'Send Feedback / Request' : view === 'scheduler' ? 'Talk to a Human' : 'Contact Team';
  const description =
    view === 'feedback'
      ? 'Share ideas, bugs, or requests with the team.'
      : view === 'scheduler'
      ? 'Pick a time in Google Calendar. The call will be hosted on Google Meet.'
      : 'Choose how you want to reach the team.';

  const triggerButton = (
    <button type="button" className={className} onClick={openModal}>
      <MessageCircle size={15} aria-hidden="true" />
      {label}
    </button>
  );

  return (
    <>
      {triggerVariant === 'inline-email' ? (
        <div className="w-full">
          {triggerButton}
          <div className="sr-only" aria-hidden="true">
            {inlineEmailPlaceholder}
          </div>
        </div>
      ) : (
        triggerButton
      )}

      {isMounted && isOpen
        ? createPortal(
            <div className="fixed inset-0 z-[90] bg-black/45 p-3 backdrop-blur-[2px] md:p-6" onClick={closeModal}>
              <div
                className="mx-auto mt-2 max-h-[calc(100vh-1rem)] w-full max-w-[980px] overflow-hidden rounded-[16px] border border-[var(--qcl-border)] bg-[var(--qcl-card)] shadow-[0_18px_70px_rgba(8,18,35,0.45)] md:mt-8 md:max-h-[calc(100vh-4rem)]"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 border-b border-[var(--qcl-border)] px-4 py-3 md:px-6 md:py-4">
                  <div>
                    <h3 id={titleId} className="text-[18px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">
                      {title}
                    </h3>
                    <p id={descriptionId} className="mt-1 text-[13px] text-[var(--qcl-text-dim)]">
                      {description}
                    </p>
                  </div>
                  <button
                    ref={closeButtonRef}
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] border border-[var(--qcl-border)] text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                    onClick={closeModal}
                    aria-label="Close dialog"
                  >
                    <X size={18} aria-hidden="true" />
                  </button>
                </div>

                {view === 'menu' ? (
                  <div className="space-y-3 p-4 md:p-6">
                    {capturedEmail ? (
                      <div className="inline-flex items-center gap-2 rounded-full bg-[var(--qcl-bg)] px-3 py-1.5 text-[12px]">
                        <span className="font-semibold text-[var(--qcl-text-dim)]">Signed in</span>
                        <span className="font-medium text-[var(--qcl-text)]">{capturedEmail}</span>
                      </div>
                    ) : null}

                    <div className="grid gap-2 md:grid-cols-2">
                      <button
                        type="button"
                        className="flex min-h-[44px] items-center gap-2.5 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-left text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                        onClick={() => {
                          setFeedbackError(null);
                          setFeedbackSuccess(false);
                          setView('feedback');
                        }}
                      >
                        <Mail size={16} aria-hidden="true" />
                        Send Feedback / Request
                      </button>

                      <button
                        type="button"
                        className="flex min-h-[44px] items-center gap-2.5 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-left text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                        onClick={() => {
                          setEmbedLoaded(false);
                          setFallbackOpened(false);
                          setView('scheduler');
                        }}
                      >
                        <CalendarClock size={16} aria-hidden="true" />
                        Talk to a Human
                      </button>

                      <button
                        type="button"
                        className="flex min-h-[44px] items-center gap-2.5 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-left text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                        onClick={() => {
                          closeModal();
                          openAppWithWebFallback(CONTACT_TEAM_WHATSAPP_DEEP_LINK, CONTACT_TEAM_WHATSAPP_URL);
                        }}
                      >
                        <Phone size={16} aria-hidden="true" />
                        WhatsApp Chat
                        <ExternalLink size={13} aria-hidden="true" className="ml-auto" />
                      </button>

                      <button
                        type="button"
                        className="flex min-h-[44px] items-center gap-2.5 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-left text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                        onClick={() => {
                          closeModal();
                          const xDmUrl = (CONTACT_TEAM_X_DM_URL || '').trim();
                          const xDmDeepLink = (CONTACT_TEAM_X_DM_DEEP_LINK || '').trim();
                          if (xDmUrl) {
                            window.location.assign(xDmUrl);
                          } else {
                            openAppWithWebFallback(xDmDeepLink, xDmUrl);
                          }
                        }}
                      >
                        <MessageSquare size={16} aria-hidden="true" />
                        X (Twitter) DM
                        <ExternalLink size={13} aria-hidden="true" className="ml-auto" />
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-muted)] transition hover:bg-[var(--qcl-bg)]"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}

                {view === 'feedback' ? (
                  <div className="space-y-3 p-4 md:p-6">
                    <button
                      type="button"
                      className="inline-flex min-h-[34px] items-center gap-1 rounded-[8px] border border-[var(--qcl-border)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                      onClick={() => setView('menu')}
                    >
                      ← Back
                    </button>

                    {capturedEmail ? (
                      <div className="inline-flex items-center gap-2 rounded-full bg-[var(--qcl-bg)] px-3 py-1.5 text-[12px]">
                        <span className="font-semibold text-[var(--qcl-text-dim)]">From</span>
                        <span className="font-medium text-[var(--qcl-text)]">{capturedEmail}</span>
                      </div>
                    ) : (
                      <div className="rounded-[10px] border border-[var(--qcl-border)] bg-[var(--qcl-bg)] p-3">
                        <div className="text-[13px] text-[var(--qcl-text-dim)]">
                          Log in to send feedback so we can follow up with you.
                        </div>
                        <a
                          href="/login"
                          className="mt-2 inline-flex min-h-[34px] items-center rounded-[8px] border border-[var(--qcl-border)] px-3 py-1.5 text-[12px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-card-hover)] hover:text-[var(--qcl-text)]"
                        >
                          Go to login
                        </a>
                      </div>
                    )}

                    <div>
                      <textarea
                        className="min-h-[150px] w-full rounded-[10px] border border-[var(--qcl-border)] bg-[var(--qcl-card)] px-3 py-2 text-[13px] text-[var(--qcl-text)] outline-none transition focus:border-[var(--qcl-accent)]"
                        placeholder="Write your feedback or request..."
                        value={feedbackMessage}
                        onChange={(event) => {
                          setFeedbackMessage(event.target.value);
                          if (feedbackError) setFeedbackError(null);
                          if (feedbackSuccess) setFeedbackSuccess(false);
                        }}
                        maxLength={FEEDBACK_MAX_CHARS}
                      />
                      <div className="mt-1 text-right text-[11px] text-[var(--qcl-text-muted)]">
                        {feedbackMessage.trim().length}/{FEEDBACK_MAX_CHARS}
                      </div>
                    </div>

                    {feedbackError ? (
                      <div className="rounded-[10px] bg-[rgba(236,72,128,0.1)] px-3 py-2 text-[12px] text-[var(--qcl-red,#ec4880)]">
                        {feedbackError}
                      </div>
                    ) : null}

                    {feedbackSuccess ? (
                      <div className="rounded-[10px] bg-[rgba(6,182,140,0.12)] px-3 py-2 text-[12px] text-[var(--qcl-green,#06b68c)]">
                        Thanks. Your feedback has been sent to the team.
                      </div>
                    ) : null}

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleFeedbackSubmit()}
                        disabled={feedbackBusy}
                        className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] bg-[var(--qcl-accent)] px-3 py-2 text-[13px] font-semibold text-white transition hover:bg-[var(--qcl-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {feedbackBusy ? 'Sending...' : 'Send'}
                      </button>
                      <button
                        type="button"
                        className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-muted)] transition hover:bg-[var(--qcl-bg)]"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : null}

                {view === 'scheduler' ? (
                  <div className="space-y-3 p-4 md:p-6">
                    <button
                      type="button"
                      className="inline-flex min-h-[34px] items-center gap-1 rounded-[8px] border border-[var(--qcl-border)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                      onClick={() => setView('menu')}
                    >
                      ← Back
                    </button>

                    {capturedEmail ? (
                      <div className="inline-flex items-center gap-2 rounded-full bg-[var(--qcl-bg)] px-3 py-1.5 text-[12px]">
                        <span className="font-semibold text-[var(--qcl-text-dim)]">Email</span>
                        <span className="font-medium text-[var(--qcl-text)]">{capturedEmail}</span>
                      </div>
                    ) : null}

                    {!embedLoaded ? (
                      <div className="rounded-[10px] bg-[var(--qcl-bg)] px-3 py-2 text-[12px] text-[var(--qcl-text-dim)]" role="status" aria-live="polite">
                        Loading scheduler...
                      </div>
                    ) : null}

                    <div className="overflow-hidden rounded-[12px] border border-[var(--qcl-border)]">
                      <iframe
                        src={TALK_TO_HUMAN_BOOKING_EMBED_URL}
                        title="QuantCheck Labs Google Calendar appointment scheduling"
                        className="h-[560px] w-full bg-[var(--qcl-card)] md:h-[620px]"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        onLoad={() => setEmbedLoaded(true)}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                        onClick={() => {
                          setFallbackOpened(true);
                          openExternalLink(TALK_TO_HUMAN_BOOKING_URL);
                        }}
                      >
                        Open in New Tab
                        <ExternalLink size={14} aria-hidden="true" />
                      </button>
                      {showSubscribeDirect ? (
                        <a
                          className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] border border-[var(--qcl-border)] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
                          href={TALK_TO_HUMAN_SUBSCRIBE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeModal}
                        >
                          Subscribe directly
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="inline-flex min-h-[38px] items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-semibold text-[var(--qcl-text-muted)] transition hover:bg-[var(--qcl-bg)]"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>

                    {fallbackOpened ? (
                      <div className="rounded-[10px] bg-[var(--qcl-bg)] px-3 py-2 text-[12px] text-[var(--qcl-text-dim)]">
                        Scheduler opened in a new tab. Use this fallback only if the embedded version doesn&apos;t work in
                        your browser.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
